import React, { useState, useEffect } from 'react';
import { Play, Users, Clock, AlertTriangle, XCircle, MoreHorizontal, RefreshCw } from 'lucide-react';
import { roomsAPI } from '../utils/api';

const LiveSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('ACTIVE'); // 'ACTIVE' or 'ALL'

  useEffect(() => {
    fetchSessions();
    // Auto refresh active sessions every 15 seconds
    const interval = setInterval(() => {
      fetchSessions(true);
    }, 15000);
    return () => clearInterval(interval);
  }, [activeFilter]);

  const fetchSessions = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      // Use active filter to determine which API to call
      const res = activeFilter === 'ALL' 
          ? await roomsAPI.getAllRooms() 
          : await roomsAPI.getActiveRooms();
          
      if (res.data.success) {
        setSessions(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching sessions:", err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleEndSession = async (id) => {
    if (window.confirm("Are you sure you want to forcibly terminate this live room?")) {
      try {
        const res = await roomsAPI.endRoom(id);
        if (res.data.success) {
          fetchSessions();
        }
      } catch (err) {
        alert("Failed to end stream session: " + (err.response?.data?.message || err.message));
      }
    }
  };

  const getDuration = (startedAt, endedAt) => {
    if (!startedAt) return '00:00';
    const start = new Date(startedAt);
    const end = endedAt ? new Date(endedAt) : new Date();
    const diffMs = end.getTime() - start.getTime();
    if (diffMs < 0) return '00:00';
    const diffSecs = Math.floor(diffMs / 1000);
    const hours = Math.floor(diffSecs / 3600);
    const mins = Math.floor((diffSecs % 3600) / 60);
    const secs = diffSecs % 60;
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">Live Monitoring</h2>
          <p className="text-slate-500 mt-1">Monitor active streams and review historical broadcast logs.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-100 p-1 rounded-xl">
            <button 
              onClick={() => setActiveFilter('ACTIVE')}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${activeFilter === 'ACTIVE' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Active Streams
            </button>
            <button 
              onClick={() => setActiveFilter('ALL')}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${activeFilter === 'ALL' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              All Logs
            </button>
          </div>
          <button 
            onClick={() => fetchSessions()}
            className="p-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition shadow-sm active:scale-95"
            title="Refresh list"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Grid Content */}
      {loading ? (
        <div className="p-20 text-center bg-white rounded-3xl border border-slate-100 shadow-sm">
          <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500 font-semibold">Loading live sessions...</p>
        </div>
      ) : sessions.length === 0 ? (
        <div className="p-20 text-center bg-white rounded-3xl border border-slate-100 shadow-sm text-slate-400">
          <Play size={48} className="mx-auto mb-4 opacity-30" />
          <p className="font-semibold text-lg">No streams found.</p>
          <p className="text-sm text-slate-400 mt-1">There are no {activeFilter === 'ACTIVE' ? 'active' : 'recorded'} streams on the server.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {sessions.map((session) => {
            const isLive = session.is_active;
            // Map a stable avatar seed based on host ID or name string hash
            const seed = session.owner_name ? session.owner_name.charCodeAt(0) % 20 : 1;
            
            return (
              <div key={session.id} className="group relative bg-white rounded-[24px] overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 flex flex-col justify-between">
                
                {/* Visual Thumbnail Area */}
                <div className="relative h-48 bg-slate-900 overflow-hidden shrink-0">
                  <img 
                    src={`https://i.pravatar.cc/300?img=${seed}`} 
                    alt="Stream Avatar" 
                    className="w-full h-full object-cover opacity-75 group-hover:opacity-55 transition-all duration-300" 
                  />
                  
                  {/* Overlay Stats */}
                  <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-md text-white text-xs px-2.5 py-1 rounded-lg flex items-center gap-1.5 font-semibold">
                    <Users size={12} /> {session.active_count || 0}
                  </div>
                  
                  <div className={`absolute top-3 right-3 text-white text-[10px] px-2.5 py-1 rounded-lg font-extrabold uppercase tracking-wider ${
                    isLive ? 'bg-red-500 animate-pulse' : 'bg-slate-600'
                  }`}>
                    {isLive ? 'LIVE' : 'ENDED'}
                  </div>
                  
                  <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-md text-white text-xs px-2.5 py-1 rounded-lg flex items-center gap-1.5 font-semibold">
                    <Clock size={12} /> {getDuration(session.created_at, session.updated_at)}
                  </div>

                  {/* Force End Action (Hover Overlay) */}
                  {isLive && (
                    <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 bg-slate-950/20 backdrop-blur-xs">
                      <button 
                        onClick={() => handleEndSession(session.id)}
                        className="bg-red-600 text-white px-4 py-2 rounded-xl text-xs font-bold hover:bg-red-700 shadow-lg shadow-red-200 transform hover:scale-105 transition flex items-center gap-1"
                        title="Force End Stream"
                      >
                        <XCircle size={16} /> End Session
                      </button>
                    </div>
                  )}
                </div>

                {/* Host Details */}
                <div className="p-5 flex-1 flex flex-col justify-between">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full p-[2px] bg-gradient-to-tr from-pink-500 to-violet-500 shrink-0">
                        <img src={session.avatar_url || `https://i.pravatar.cc/100?img=${seed}`} className="w-full h-full rounded-full border-2 border-white object-cover" alt="" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-bold text-slate-800 text-sm truncate">{session.name || session.owner_name || 'Room'}</h4>
                        <p className="text-[10px] text-slate-400 truncate">Host: {session.owner_name}</p>
                        <p className="text-[10px] text-slate-400 truncate">ID: {session.owner_xoxo_id}</p>
                      </div>
                    </div>
                  </div>

                  {/* Status Bar */}
                  <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    <span className={isLive ? 'text-green-500' : 'text-slate-400'}>
                      {isLive ? 'Excellent Health' : 'Completed'}
                    </span>
                    <span>Asia Pacific</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LiveSessions;